/**
 * Reusable Form Component (implementation)
 *
 * A form wrapper that prevents native HTML form submission (which causes page refresh).
 */

import Box from '@mui/material/Box';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import type { FormEvent, ForwardedRef } from 'react';
import type { FormProps, FormRef } from './Form.types';

export const FormRoot = forwardRef<FormRef, FormProps>(function FormRoot(
    { onSubmit, children, id, disabled = false, sx, ...boxProps },
    ref: ForwardedRef<FormRef>
) {
    const formElementRef = useRef<HTMLFormElement>(null);

    const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!disabled) {
            onSubmit();
        }
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            if (!disabled) {
                onSubmit();
            }
        },
        reset: () => {
            formElementRef.current?.reset();
        },
    }));

    const sxProps = sx ? { sx } : {};

    return (
        <Box
            ref={formElementRef}
            component="form"
            onSubmit={handleFormSubmit}
            id={id}
            {...sxProps}
            {...boxProps}
        >
            {children}
        </Box>
    );
});


